/**
 * Phase 14.5 Step 2 — Perturbation section (Phase 10.1).
 *
 * Toggle + collapsed registry-driven panel. Form data is held by the
 * wizard's react-hook-form state; this component is a controlled
 * leaf. Reads the live `pk-campaigns/registry` payload (33 variables
 * grouped by category) and surfaces the variable count. Step 3 of
 * Phase 14.5 may swap the preset chips for the existing
 * `VariablesTab.jsx` editor; for Step 2 we ship the scaffold so the
 * dispatch lane resolves and the registry render is visible.
 */
import { Sliders } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const PRESET_OPTIONS = [
    { id: "nominal",    label: "Nominal — platform defaults" },
    { id: "stressing",  label: "Stressing" },
    { id: "worst_case", label: "Worst-case" },
    { id: "custom",     label: "Custom (per-variable, Step 3)" },
];

function _categoriesFromRegistry(reg) {
    if (!reg) return [];
    // Phase 10.1 registry shape: { variables: [{id, category, ...}, ...] }
    // Be tolerant — accept either `variables` or `entries` or
    // `categories` shapes so any minor schema drift doesn't crash
    // the wizard.
    const arr = reg.variables || reg.entries || reg.catalog || [];
    if (!Array.isArray(arr) || arr.length === 0) {
        // Fallback: registry may already be grouped.
        if (reg.by_category && typeof reg.by_category === "object") {
            return Object.entries(reg.by_category).map(([cat, items]) => ({
                category: cat,
                count: Array.isArray(items) ? items.length : 0,
            }));
        }
        return [];
    }
    const buckets = new Map();
    for (const v of arr) {
        const cat = v?.category || "uncategorised";
        buckets.set(cat, (buckets.get(cat) || 0) + 1);
    }
    return Array.from(buckets, ([category, count]) => ({ category, count }));
}

export default function PerturbationSection({
    enabled = false,
    onToggleEnabled,
    value,
    onChange,
    registry,
}) {
    const preset = value?.preset || "nominal";
    const categories = _categoriesFromRegistry(registry);
    const totalVars = categories.reduce((s, c) => s + c.count, 0);

    return (
        <section
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
            data-testid="section-perturbation"
            data-enabled={enabled ? "1" : "0"}
        >
            <div className="flex items-start gap-3">
                <Switch
                    checked={enabled}
                    onCheckedChange={onToggleEnabled}
                    data-testid="toggle-perturbation"
                    aria-label="Enable perturbation"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Sliders className="size-4 text-ux-text-3" />
                        <span className="text-sm font-medium text-ux-text-1">Perturbation</span>
                    </div>
                    <p className="text-xs text-ux-text-3">
                        Sample variables across distributions during the campaign — turns
                        a vacuous baseline into a perturbed baseline.
                    </p>
                </div>
            </div>

            {enabled && (
                <div className="mt-4 space-y-3" data-testid="perturbation-details">
                    <div
                        className="text-xs text-ux-text-3"
                        data-testid="perturbation-registry-summary"
                    >
                        Registry catalogue: <strong className="text-ux-text-2">{totalVars}</strong>{" "}
                        variables across <strong className="text-ux-text-2">{categories.length}</strong>{" "}
                        categories.
                    </div>
                    <div>
                        <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            Preset
                        </Label>
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Perturbation preset">
                            {PRESET_OPTIONS.map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={preset === opt.id}
                                    data-testid={`perturbation-preset-${opt.id}`}
                                    onClick={() => onChange?.({ ...(value || {}), preset: opt.id })}
                                    className={[
                                        "rounded-full border px-3 py-1 text-xs transition-colors",
                                        preset === opt.id
                                            ? "border-ux-text-1 bg-ux-surface-2 text-ux-text-1"
                                            : "border-ux-border-1 bg-ux-surface-1 text-ux-text-3 hover:bg-ux-surface-2/60",
                                    ].join(" ")}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <ul
                        className="grid grid-cols-2 gap-1 text-[11px] text-ux-text-3 sm:grid-cols-3"
                        data-testid="perturbation-categories"
                    >
                        {categories.map((c) => (
                            <li
                                key={c.category}
                                className="truncate rounded bg-ux-surface-2/40 px-2 py-1"
                                data-testid={`perturbation-category-${c.category}`}
                            >
                                {c.category}: {c.count}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
